import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LuBuilding2,
  LuCalendar,
  LuDatabase,
  LuEye,
  LuFileText,
  LuPencil,
  LuPhone,
  LuReceipt,
  LuTrash2,
  LuUser,
} from "react-icons/lu";

import Button from "../ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardClass,
  cardContentFlushClass,
  detailTableHeadRowClass,
  detailTableWrapClass,
  mobileCardClass,
  moneyCellClass,
  tableHeadCellClass,
} from "../ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useConfirmPopup from "../../context/useConfirmPopup";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { makeReport } from "../../services/download.service";
import {
  formatAmount,
  formatDateTime,
  formatQty,
} from "../../static/materialLine";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { VmcReceiveListProps } from "../../types/vmcReceive";

const actionSpinnerClass = cx(
  buttonSpinnerClass,
  "mr-0 border-[#113F67]/30 border-t-[#113F67]"
);

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const actionGridClass: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

const partyLinkClass = "font-semibold text-primary hover:underline break-words";

const contactIconClass = "h-3.5 w-3.5 flex-shrink-0 text-gray-400";

const detailPath = (id: number) =>
  route.vmcReceiveDetail.replace(":id", id as unknown as string);

const editPath = (id: number) =>
  route.vmcReceiveEdit.replace(":id", id as unknown as string);

const partyPath = (id: number) =>
  route.partyDetail.replace(":id", id as unknown as string);

// The contact name goes under the firm name; a party without a firm name shows its contact name in its place.
const contactName = (receive: VmcReceiveListProps) =>
  receive.party_firm_name ? receive.party_name : null;

type Props = {
  receives: VmcReceiveListProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  handleDeleteReceive: (id: number) => void;
  // Inside a detail card (the VMC order page): a bordered table with a tinted header row, like the material order's.
  embedded?: boolean;
  emptyLabel?: string;
};

const defaultEmptyLabel = "VMC રિસીવ ની વિગતો મળી નથી";

const VmcReceiveList = ({
  receives,
  pageNumber,
  pageSize,
  isLoading,
  handleDeleteReceive,
  embedded = false,
  emptyLabel = defaultEmptyLabel,
}: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirmDelete = useConfirmPopup();
  const loggingUser = useAppSelector(getLoggingUser);

  // The VMC order page shows its receives to users who can't open them.
  const canRead = hasPermission(
    loggingUser?.permissions,
    permission.vmcReceivesRead
  );
  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.vmcReceivesUpdate
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.vmcReceivesDelete
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.vmcReceivesReport
  );
  // The amount column is only for a user who may see rates.
  const canRate = hasPermission(
    loggingUser?.permissions,
    permission.vmcRate
  );

  const columnCount = canRate ? 7 : 6;
  const actionCount =
    (canRead ? 1 : 0) +
    (canReport ? 1 : 0) +
    (canUpdate ? 1 : 0) +
    (canDelete ? 1 : 0);

  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો? પાર્ટી એકાઉન્ટ માંથી પણ આ રિસીવ નીકળી જશે.`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (confirmed) {
      setIsActionLoading(id);
      await handleDeleteReceive(id);
      setIsActionLoading(0);
    }
  };

  const handleExportReport = async (id: number) => {
    setIsActionLoading(id);
    const file = await makeReport(`/v1/admin/vmc-receives/${id}/pdf`);
    setIsActionLoading(0);

    if (file) dispatch(updatePdfFile(file));
  };

  const receiveNumber = (receive: VmcReceiveListProps) => (
    <span className="inline-flex items-center gap-2 font-semibold text-primary">
      <LuReceipt className="h-4 w-4 flex-shrink-0 text-gray-400" />
      <span>#{receive.id}</span>
    </span>
  );

  const partyDetails = (
    receive: VmcReceiveListProps,
    isMobile: boolean
  ) => {
    const contact = contactName(receive);

    return (
      <div className={cx("min-w-0", isMobile && "text-right")}>
        <Link
          to={partyPath(receive.party_id)}
          target="_blank"
          rel="noopener noreferrer"
          className={partyLinkClass}
        >
          {receive.party_firm_name || receive.party_name || "-"}
        </Link>

        {(contact || receive.party_phone) && (
          <div
            className={cx(
              "mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-gray-500",
              isMobile && "justify-end"
            )}
          >
            {contact && (
              <span className="flex items-center gap-1 break-words">
                <LuUser className={contactIconClass} />
                {contact}
              </span>
            )}

            {receive.party_phone && (
              <span className="flex items-center gap-1">
                <LuPhone className={contactIconClass} />
                {receive.party_phone}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const itemCount = (receive: VmcReceiveListProps) =>
    `${formatQty(receive.items_count)} આઈટમ`;

  const actionButtons = (
    receive: VmcReceiveListProps,
    isMobile: boolean
  ) => {
    const buttonProps = isMobile
      ? { variant: "outline" as const, className: "w-full" }
      : { variant: "ghost" as const, size: "iconSm" as const };

    return (
      <>
        {canRead && (
          <Link to={detailPath(receive.id)}>
            <Button {...buttonProps} title="વિગતો જુઓ">
              <LuEye className="h-4 w-4" />
            </Button>
          </Link>
        )}

        {canReport && (
          <Button
            {...buttonProps}
            title="રિપોર્ટ એક્સપોર્ટ"
            className={cx(
              buttonProps.className,
              "text-green-600 hover:text-green-700"
            )}
            onClick={() => handleExportReport(receive.id)}
          >
            <LuFileText className="h-4 w-4" />
          </Button>
        )}

        {canUpdate && (
          <Button
            {...buttonProps}
            title="સંપાદિત કરો"
            onClick={() => navigate(editPath(receive.id))}
          >
            <LuPencil className="h-4 w-4" />
          </Button>
        )}

        {canDelete && (
          <Button
            {...buttonProps}
            title="કાઢી નાખો"
            className={cx(
              buttonProps.className,
              "text-red-600 hover:text-red-700"
            )}
            onClick={() => handleDeleteRow(receive.id)}
          >
            <LuTrash2 className="h-4 w-4" />
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      <div
        className={cx(embedded ? detailTableWrapClass : cardClass, "max-sm:hidden")}
      >
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className={embedded ? detailTableHeadRowClass : undefined}>
                  <TableHead className={cx(tableHeadCellClass, embedded && "w-20")}>
                    ક્રમ
                  </TableHead>
                  <TableHead className={tableHeadCellClass}>રિસીવ</TableHead>
                  <TableHead className={tableHeadCellClass}>પાર્ટી</TableHead>
                  <TableHead className={tableHeadCellClass}>આઈટમ</TableHead>
                  {canRate && (
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      રકમ
                    </TableHead>
                  )}
                  <TableHead className={tableHeadCellClass}>તારીખ</TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    એકશન્સ બટન
                  </TableHead>
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={columnCount} />
              ) : receives.length === 0 ? (
                <TableEmptyRow colSpan={columnCount} label={emptyLabel} />
              ) : (
                <TableBody>
                  {receives.map((receive, index) => (
                    <TableRow key={receive.id}>
                      <TableCell className="font-semibold text-primary">
                        {(pageNumber - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell>{receiveNumber(receive)}</TableCell>

                      <TableCell>
                        <div className="flex items-start gap-2">
                          <LuBuilding2 className="mt-1 h-4 w-4 flex-shrink-0 text-gray-400" />
                          {partyDetails(receive, false)}
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="font-medium text-primary">
                          {itemCount(receive)}
                        </div>
                        <div className="text-sm text-gray-500">
                          કુલ નંગ: {formatQty(receive.total_qty)}
                        </div>
                      </TableCell>

                      {canRate && (
                        <TableCell className={moneyCellClass}>
                          {formatAmount(receive.total)}
                        </TableCell>
                      )}

                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1">
                            <LuCalendar className="h-3.5 w-3.5 text-blue-800" />
                            {formatDateTime(receive.date)}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-gray-400">
                            <LuDatabase className="h-3.5 w-3.5 text-gray-400" />
                            {formatDateTime(receive.created_at)}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isActionLoading === receive.id ? (
                            <div className={actionSpinnerClass}></div>
                          ) : (
                            actionButtons(receive, false)
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : receives.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label={emptyLabel} />
          </div>
        ) : (
          receives.map((receive, index) => (
            <div key={receive.id} className={mobileCardClass}>
              <div className="flex items-center justify-between gap-2 border-b p-4">
                {receiveNumber(receive)}
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-semibold text-primary">
                    {(pageNumber - 1) * pageSize + index + 1}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>પાર્ટી</span>
                  {partyDetails(receive, true)}
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>આઈટમ</span>
                  <span className="text-right">
                    <span className="font-medium text-primary">
                      {itemCount(receive)}
                    </span>
                    <span className="block text-sm text-gray-500">
                      કુલ નંગ: {formatQty(receive.total_qty)}
                    </span>
                  </span>
                </div>

                {canRate && (
                  <div className={cardRowClass}>
                    <span className={cardLabelClass}>રકમ</span>
                    <span className={moneyCellClass}>
                      {formatAmount(receive.total)}
                    </span>
                  </div>
                )}

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDateTime(receive.date)}
                    <span className="block text-xs text-gray-400">
                      {formatDateTime(receive.created_at)}
                    </span>
                  </span>
                </div>
              </div>

              {actionCount > 0 && (
                <div
                  className={cx(
                    "grid gap-2 border-t p-4",
                    actionGridClass[actionCount]
                  )}
                >
                  {isActionLoading === receive.id ? (
                    <div className="col-span-full flex h-10 items-center justify-center">
                      <div className={actionSpinnerClass}></div>
                    </div>
                  ) : (
                    actionButtons(receive, true)
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default VmcReceiveList;
